import { Link } from "react-router-dom"
import { Button } from "./ui/button"
import Wave from 'react-wavify'
import { ChevronDown, Clapperboard, Disc3, Forward, List, Mic2, Music2, Pencil, ShipIcon, Tv, UserPlus, X } from "lucide-react"
import { motion } from "motion/react"
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover"
import LoginDialog from "./LoginDialog"
import spotifyLogo from '../assets/images/spotify-logo.svg';
import tmdbLogo from '../assets/images/tmdb-logo.svg';
import { useMutation, useQueryClient } from "@tanstack/react-query"
import { AuthVerify } from "./ApiRoutes"
import { toast } from "sonner"
import { useEffect, useState } from "react"


export default function Landing (){
    document.title ="MediaHarbor - Welcome :)"
    const queryClient = useQueryClient();

    const [isAuth, setIsAuth] = useState<boolean>(false);

    useEffect(() => {
       
        if(localStorage.getItem("accessToken")){
            postVerify.mutate();
        }
        
    },[window.location])

    const postVerify = useMutation({
            mutationFn: () => 
                AuthVerify(localStorage.getItem("accessToken") ?? ""),

            onSuccess: (data, variables) => {
                setIsAuth(true);
                toast.success("Re-authenticated. Welcome back " + localStorage.getItem("username") + ".")
            },
        });
    

    return(
        <main className="flex flex-col min-h-svh w-full items-center justify-center scroll-smooth">
            <Banner title="Disclaimer" message=" This is a pre-release build for testing. We cannot gurantee data integrity and are not responsible for data loss.
                There maybe minor or major issues, bugs, and or missing features that may hinder or completely disable use of the platform." />
            <Hero auth={isAuth} />
            <div className="bg-black flex w-full h-full justify-center items-center">
                <div className="flex flex-col bg-black w-full px-5 max-w-300">
                    <Actions />
                    <UnderWave />
                    <Lists />
                    <ConsumeLater />
                    <DataProviders />
                    <Canada />
                </div>
            </div>
        </main>
    )
}


interface HeroProps {
    auth:boolean;
}

function Hero ({auth}:HeroProps){
     const handleScroll = () => {
        const element = document.getElementById('start');
        if (element) {
        element.scrollIntoView({ behavior: 'smooth' });
        }
    };


    return(
        <section className="flex flex-col w-full h-svh bg-neutral-200 ease-in-out duration 150 gap-2">
            <div className="flex flex-col items-center h-full justify-center text-black px-2">
                <motion.span 
                initial={{y:0}}
                animate={{
                    y:[-5, -10, -2],
                    rotate:[-5, 3, -8, 6 ],
                     transition: {
                        repeat: Infinity,
                        repeatType: "reverse",
                        type:"tween",
                        ease:"easeInOut",
                        duration:2.5,
                    },
                }} 
                className="flex" >
                    <ShipIcon />
                </motion.span>

                <h1 className="text-3xl md:text-4xl font-bold mb-2">MediaHarbor</h1>
                <p className="text-base md:text-lg mb-4">Dock all your favourite media in one place.</p>
                    <div className="flex gap-2"> 
                        { auth && localStorage.getItem("username") ?
                            <Link to={"/profile/"+localStorage.getItem("username")}>
                                <Button className="hover:cursor-pointer">My profile</Button>
                            </Link>
                            :
                            <LoginDialog>
                                <Button className="hover:cursor-pointer">Get started</Button>
                            </LoginDialog>
                        }
                        <Link to={"/profile/mat"}>
                            <Button variant="outline" className=" hover:cursor-pointer">Example profile</Button>
                        </Link>
                    </div>
                    
            </div>
            <motion.div className="flex justify-center"
            initial={{ opacity: 0 }}  animate={{ 
                opacity: 1,
                transition: {delay:1.75, duration:0.5}
                
             }}
            >
                <ChevronDown onClick={handleScroll} className="opacity-100 delay-100 cursor-pointer ease-in-out duration-125 hover:scale-110" />
            </motion.div>
            

            <Wave
            className=""
            fill='rgba(0, 0, 0, 1)'
            paused={false}
            options={{
            
                height: 50,
                amplitude: 45,
                speed: 0.15,
                points: 3
            }} />
        </section>
    )
}

function UnderWave () {
    const headingStyles = "text-2xl md:text-3xl font-bold mb-2";
    const iconSize = "h-5 w-5";
    return(
        <div id={"under-wave"} className="flex  bg-black w-full h-dvh">
        <div className="flex h-20"></div>
        <motion.section 
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            transition={{ delay:0.2, duration: 0.5, ease:"easeInOut" }}
            variants={{
                visible: { opacity: 1 },
                hidden: { opacity: 0 }
            }}
        className="flex flex-col h-full w-full bg-black text-white justify-center p-4 max-w-200 md:pl-16">
            <div className="flex justify-left pl-1 gap-4 mb-2">
                <Disc3 className={iconSize} />
                <Tv className={iconSize} />
                <Clapperboard className={iconSize} />
                <Music2 className={iconSize} />
                <Mic2 className={iconSize} />
            </div>
            <h2 className={`${headingStyles}`}>All kinds of media</h2>
            <p>We consume numerous forms of media each day, multiple times a day.<br /> <br /> Show off and share all your favourite songs, artists, albums, shows, and movies <span className="italic text-sm">(with more coming soon)</span>  in one convienent place. </p>
            
        </motion.section>
        </div>
    )
}

function Lists() {
    const listImageClasses = "h-fit";
    const headingStyles = "text-2xl md:text-3xl font-bold mb-2 pr-6 text-right";

    const listUrls = ["list/view/27","list/view/2" ]
    return(
        <motion.section className="flex flex-col justify-center items-end h-dvh bg-black text-white px-5 md:pr-16"
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            transition={{ delay:0.2, duration: 0.5, ease:"easeInOut" }}
            variants={{
                visible: { opacity: 1 },
                hidden: { opacity: 0 }
            }}
        >
            
            <h2 className={headingStyles}>
                Create and share lists
            </h2>
            <p className="pr-6 mb-4 text-right ">Create, tag, describe, and rank lists of any type of media for any reason.</p>
            <Link to={listUrls[Math.floor(Math.random()*listUrls.length)]}>
                <Button variant={"outline"} className="mr-6 text-black hover:cursor-pointer">View a random list</Button>
            </Link>
        
        </motion.section>
    )
}


function ConsumeLater() {
    const headingStyles = "text-2xl md:text-3xl font-bold mb-2 pr-6";

    return(
        <motion.section className="flex flex-col justify-center items-start max-w-200 h-dvh bg-black text-white px-5 md:pl-16"
        initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            transition={{ delay:0.2, duration: 0.5, ease:"easeInOut" }}
            variants={{
                visible: { opacity: 1 },
                hidden: { opacity: 0 }
            }}
        >
            <h2 className={headingStyles}>
                Keep track of your media with <span className="underline">Consume Later</span>
            </h2>
            <p>
                Artists, movies, music, it doesn't matter. Consolidate all your friends recommendations into one list and get them off your back.
            </p>
        </motion.section>
    )
}

function Actions() {
    const headingStyles = "text-2xl md:text-3xl font-bold mb-2";
    const iconClasses = "w-8 h-8";
    const iconBGClasses = "rounded-full p-2 flex flex-col items-center gap-1 font-semibold justify-center hover:cursor-pointer" ;
    const popoverClasses = "bg-black text-white border-2 border-white";
    return(
        <motion.section id="start" className="flex flex-col justify-center items-center w-full h-dvh bg-black text-white mt-35 px-5"
        initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            transition={{ delay:0.3, duration: 0.5, ease:"easeInOut" }}
            variants={{
                visible: { opacity: 1 },
                hidden: { opacity: 0 }
            }}
        >
         
            <h2 className={headingStyles}>Let's keep it simple.</h2>   
            <article className="flex gap-4 justify-center items-center ">
               
                <Popover>
                    <PopoverTrigger>
                        <motion.div 
                        className={iconBGClasses}
                        whileInView="visible"
                        viewport={{ once: true }}
                        transition={{ delay:1, duration: 0.5, ease:"easeInOut" }}
                        variants={{
                            visible: { opacity: 1 },
                            hidden: { opacity: 0 }
                        }}
                        >
                            <UserPlus className={iconClasses}/>
                            <h3>Create</h3>
                        </motion.div>
                    </PopoverTrigger>
                        <PopoverContent className={popoverClasses}>
                            <p>Create profiles and unique lists.</p>
                        </PopoverContent>
                </Popover>
                 <Popover>
                    <PopoverTrigger>
                        <motion.div className={iconBGClasses}
                        whileInView="visible"
                        viewport={{ once: true }}
                        transition={{ delay:1.3, duration: 0.5, ease:"easeInOut" }}
                        variants={{
                            visible: { opacity: 1 },
                            hidden: { opacity: 0 }
                        }}
                        >
                             <Pencil className={iconClasses} />
                            <h3>Edit</h3>
                        </motion.div>
                    </PopoverTrigger>
                        <PopoverContent className={popoverClasses}>
                            <p>Edit and add to your profile, Consume Later, and other lists as your tastes grow and evolve.</p>
                        </PopoverContent>
                </Popover>
                 <Popover>
                    <PopoverTrigger>
                        <motion.div className={iconBGClasses}
                        whileInView="visible"
                        viewport={{ once: true }}
                        transition={{ delay:1.6, duration: 0.5, ease:"easeInOut" }}
                        variants={{
                            visible: { opacity: 1 },
                            hidden: { opacity: 0 }
                        }}
                        >
                            <Forward className={iconClasses} />
                            <h3>Share</h3>
                        </motion.div>
                    </PopoverTrigger>
                        <PopoverContent className={popoverClasses}>  
                            <p>Sick of giving bad answers to "what kind of music do you like?" or "what kind of shows do you watch?" Share your profile with others so they understand your actual tastes!</p>
                        </PopoverContent>
                </Popover>
            </article>
            
        </motion.section>
    )
}

function Canada() {
    return(
        <motion.section className="flex flex-col justify-center items-center gap-0.5 h-full bg-black text-white mt-35 mb-5 px-5 md:pl-16"
        initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            transition={{ delay:0.2, duration: 0.5, ease:"easeInOut" }}
            variants={{
                visible: { opacity: 1 },
                hidden: { opacity: 0 }
            }}
        >
        <h3 className="font-semibold text-base">
            Made with love
        </h3>
        <img className="rounded-full" src="https://flagsapi.com/CA/flat/32.png" />
    </motion.section>
    )
}

function DataProviders(){
    const headingStyles = "text-2xl md:text-3xl font-bold mb-2 text-center";
    return(
        <motion.section className="flex flex-col justify-center items-center h-dvh bg-black text-white mb-15 px-5"
        initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            transition={{ delay:0.4, duration: 0.5, ease:"easeInOut" }}
            variants={{
                visible: { opacity: 1 },
                hidden: { opacity: 0 }
            }}
        >
           
            <h2 className={headingStyles}>
                Our data providers
            </h2>
            <p className="text-center text-sm italic max-w-200 mb-4">This application uses TMDB and the TMDB APIs but is not endorsed, certified, or otherwise approved by TMDB.</p>
            <div className="flex flex-col md:flex-row text-white md:gap-4 justify-center items-center">
                <Link to={"https://spotify.com/"}><img className="w-40 h-15" src={spotifyLogo} ></img></Link>
                <Link to={"https://tmdb.org/"}><img className="w-40 h-15" src={tmdbLogo} ></img></Link>
            </div>

        </motion.section>
    )
}

interface BannerProps {
    title: string;
    message: string;
}

function Banner ({title,message}:BannerProps) {
    const [showBanner, setShowBanner] = useState<boolean>(localStorage.getItem(title+"Banner") !== "false");
    const onDisable = () => {
        showBanner && setShowBanner(false);
        localStorage.setItem(title+"Banner", "false");
    }
    const onClose = () => {
        showBanner && setShowBanner(false);
    }

    return(
        <motion.header 
        className={`${showBanner ? "visible" : "hidden"} 
        flex flex-col fixed top-0 z-50 w-full bg-neutral-300 text-black  p-4 border-b-2 border-neutral-400`}
        initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            transition={{ delay:0.2, duration: 0.5, ease:"easeInOut" }}
            variants={{
                visible: { opacity: 1 },
                hidden: { opacity: 0 }
            }}
        >
            <h5 className="font-semibold">
                {title}:
            </h5>
            <p className="text-xs">
               {message}
            </p>
            <div className="flex gap-1 pt-2">
                <Button onClick={onClose} variant={"outline"} className="text-xs bg-transparent w-fit px-3 py-0 hover:cursor-pointer ease-in-out duration-200 hover:text-white hover:bg-black hover:border-transparent">
                    <X className="h-3 w-3"/>
                    Close
                </Button>
                <Button variant={"link"} className="text-xs underline hover:cursor-pointer px-3 py-0 ease-in-out duration-200 hover:font-bold" onClick={onDisable}>
                    Don't show this again
                </Button>
            </div>
            
        </motion.header>
    )
}